import { useAuthStore } from "@/stores/auth";

async function request<T>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const token = useAuthStore.getState().token;
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  // Explicit Accept lets nginx reliably distinguish SPA page loads
  // (Accept: text/html) from API XHR calls (application/json).
  headers.set("Accept", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const resp = await fetch(path, { ...init, headers });
  if (resp.status === 401) {
    useAuthStore.getState().logout();
    throw new Error("unauthorized");
  }
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`${resp.status}: ${text}`);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  is_admin: boolean;
  email: string;
  display_name: string | null;
}

export interface MeOut {
  id: number;
  email: string;
  display_name: string | null;
  is_admin: boolean;
  can_use_mcp: boolean;
  created_at: string;
  last_login_at: string | null;
  password_version: number;
}

export interface MySession {
  id: number;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface MyFeedbackEntry {
  id: number;
  message_id: number;
  conversation_id: number;
  conversation_title: string;
  rating: number;
  comment: string | null;
  created_at: string;
  message_excerpt: string;
}

export interface MyStats {
  conversations: number;
  messages_sent: number;
  days_active: number;
  first_message_at: string | null;
  last_message_at: string | null;
  feedback_given: number;
}

export interface MySupport {
  id: number;
  body: string;
  status: string;
  created_at: string;
  resolved_at: string | null;
}

export interface MyApiKey {
  id: number;
  name: string;
  prefix: string;
  created_at: string;
  expires_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  is_active: boolean;
}

/** Key creation is the only time the secret is ever returned. */
export interface MyApiKeyCreated {
  key: string;
  api_key: MyApiKey;
}

export interface AdminSupport {
  id: number;
  user_id: number;
  email_snapshot: string;
  body: string;
  status: string;
  created_at: string;
  resolved_at: string | null;
  resolved_by_email: string | null;
}

export interface Conversation {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: number;
  role: "user" | "assistant" | "system";
  content: string;
  meta: Record<string, unknown> | null;
  created_at: string;
}

export interface SourceRef {
  path: string;
  source_type: string;
  score: number;
  citation: string;
  meta: Record<string, unknown>;
}

export interface ChatResponse {
  conversation_id: number;
  assistant_message_id: number;
  answer: string;
  sources: SourceRef[];
  had_context: boolean;
  rewritten_query: string;
  model: string;
  latency_ms: number;
  cost_usd: number | null;
}

export interface RuntimeSettings {
  system_prompt: string;
  llm_model: string;
  llm_fallback_model: string;
  llm_temperature: number;
  rag_top_k: number;
  rag_min_score: number;
}

export interface AdminUser {
  id: number;
  email: string;
  display_name: string | null;
  is_admin: boolean;
  is_active: boolean;
  can_use_mcp: boolean;
  created_at: string;
  last_login_at: string | null;
}

export interface AdminStats {
  questions: { day: number; week: number; month: number };
  feedback: { positive: number; negative: number; positive_pct: number | null };
  context_hit_ratio_pct: number | null;
  avg_latency_ms: number | null;
  avg_dialog_length: number;
  spend_usd: { day: number; week: number; month: number };
  top_users_30d: Array<{
    email: string;
    questions: number;
    spend_usd: number;
  }>;
  corpus: { documents: number; chunks: number };
  health?: {
    db: { ok: boolean; detail: string };
    openrouter: { ok: boolean };
    git: Record<string, unknown>;
    embedding_model: string;
  };
}

export interface OpenRouterBalance {
  ok: boolean;
  total_credits_usd?: number;
  total_usage_usd?: number;
  remaining_usd?: number;
  error?: string;
}

export interface SearchHit {
  message_id: number;
  conversation_id: number;
  conv_title: string;
  owner_email: string;
  role: string;
  snippet: string;
  created_at: string;
}

export type ExportFmt = "csv" | "json" | "md" | "txt";

export interface AdminConv {
  id: number;
  user_email: string;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export interface AdminConvDetail {
  id: number;
  title: string;
  owner_email: string;
  created_at: string;
  updated_at: string;
  messages: Array<{
    id: number;
    role: string;
    content: string;
    meta: Record<string, unknown> | null;
    created_at: string;
    feedback: { rating: number; comment: string | null } | null;
    retrieval?: {
      query: string;
      rewritten_query: string | null;
      retrieved_chunk_ids: number[] | null;
      scores: number[] | null;
      had_context: boolean | null;
      model: string | null;
      tokens_in: number | null;
      tokens_out: number | null;
      latency_ms: number | null;
    };
  }>;
}

export interface AdminDoc {
  id: number;
  path: string;
  source_type: string;
  title: string | null;
  indexed_at: string;
  chunk_count: number;
}

export interface ReindexStatus {
  running: boolean;
  result: {
    added: string[];
    updated: string[];
    unchanged: string[];
    removed: string[];
    total_chunks: number;
  } | null;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface NegativeFeedbackItem {
  message_id: number;
  conversation_id: number;
  conv_title: string | null;
  owner_email: string;
  answer: string;
  question: string | null;
  comment: string | null;
  feedback_at: string;
  answered_at: string;
}

export interface ReindexHtmlStatus {
  running: boolean;
  // Counts (not full path lists like ReindexStatus) — keeps payload tiny so
  // the UI can poll every few seconds without bandwidth concerns.
  result: {
    added: number;
    updated: number;
    unchanged: number;
    removed: number;
    total_chunks: number;
  } | null;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  progress: { processed: number; total: number; chunks: number };
}

export const api = {
  login: (email: string, password: string) =>
    request<LoginResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  listConversations: () =>
    request<Conversation[]>("/chat/conversations"),

  listMessages: (convId: number) =>
    request<Message[]>(`/chat/conversations/${convId}/messages`),

  deleteConversation: (convId: number) =>
    request<void>(`/chat/conversations/${convId}`, { method: "DELETE" }),

  sendMessage: (message: string, conversationId?: number) =>
    request<ChatResponse>("/chat/message", {
      method: "POST",
      body: JSON.stringify({
        message,
        conversation_id: conversationId ?? null,
      }),
    }),

  /**
   * Streaming version of sendMessage. Receives Server-Sent Events from
   * /chat/message/stream and dispatches them through callbacks. The promise
   * resolves once the stream completes (after `done`/`error`). Errors
   * inside the stream are signalled via `onError` AND propagated via the
   * returned promise reject — callers should choose one path.
   */
  async streamMessage(
    message: string,
    conversationId: number | undefined,
    handlers: {
      onToken?: (text: string) => void;
      onReplace?: (text: string) => void;
      onSources?: (text: string) => void;
      onThumbs?: (text: string) => void;
      onMeta?: (meta: {
        conversation_id: number;
        assistant_message_id: number;
        model: string;
        tokens_in: number;
        tokens_out: number;
        latency_ms: number;
        cost_usd: number | null;
        had_context: boolean;
      }) => void;
      onDone?: () => void;
      onError?: (err: string) => void;
      signal?: AbortSignal;
    }
  ): Promise<void> {
    const token = useAuthStore.getState().token;
    const resp = await fetch("/chat/message/stream", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        message,
        conversation_id: conversationId ?? null,
      }),
      signal: handlers.signal,
    });
    if (!resp.ok || !resp.body) {
      const text = await resp.text().catch(() => "");
      throw new Error(`${resp.status}: ${text || resp.statusText}`);
    }
    const reader = resp.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buf = "";

    // Minimal SSE parser. Each event is one or more lines terminated by a
    // blank line. We handle `event:` and `data:` (multi-line `data:` is
    // joined with \n per spec).
    const handleFrame = (raw: string) => {
      const lines = raw.split("\n");
      let evt = "message";
      const dataLines: string[] = [];
      for (const ln of lines) {
        if (ln.startsWith("event:")) {
          evt = ln.slice(6).trim();
        } else if (ln.startsWith("data:")) {
          dataLines.push(ln.slice(5).replace(/^ /, ""));
        }
      }
      if (dataLines.length === 0) return;
      const dataStr = dataLines.join("\n");
      let payload: any = dataStr;
      try {
        payload = JSON.parse(dataStr);
      } catch {
        // keep raw string
      }
      switch (evt) {
        case "token":
          handlers.onToken?.(payload.text ?? "");
          break;
        case "replace":
          handlers.onReplace?.(payload.text ?? "");
          break;
        case "sources":
          handlers.onSources?.(payload.text ?? "");
          break;
        case "thumbs":
          handlers.onThumbs?.(payload.text ?? "");
          break;
        case "meta":
          handlers.onMeta?.(payload);
          break;
        case "done":
          handlers.onDone?.();
          break;
        case "error":
          handlers.onError?.(payload.error ?? String(payload));
          break;
      }
    };

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // SSE frames separated by a blank line (\n\n).
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const frame = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          if (frame.trim()) handleFrame(frame);
        }
      }
      // Flush any trailing frame
      if (buf.trim()) handleFrame(buf);
    } finally {
      reader.releaseLock();
    }
  },

  sendFeedback: (messageId: number, rating: 1 | -1, comment?: string) =>
    request<void>("/chat/feedback", {
      method: "POST",
      body: JSON.stringify({ message_id: messageId, rating, comment: comment ?? null }),
    }),

  // Self-service ("/me")
  me: () => request<MeOut>("/me"),
  updateMe: (patch: { display_name?: string | null }) =>
    request<MeOut>("/me", { method: "PATCH", body: JSON.stringify(patch) }),
  changePassword: (old_password: string, new_password: string) =>
    request<void>("/me/password", {
      method: "POST",
      body: JSON.stringify({ old_password, new_password }),
    }),
  signOutEverywhere: () =>
    request<void>("/me/signout-all", { method: "POST" }),
  mySessions: () => request<MySession[]>("/me/sessions"),
  myFeedback: () => request<MyFeedbackEntry[]>("/me/feedback"),
  myStats: () => request<MyStats>("/me/stats"),
  myExportUrl: () => "/me/export",
  myApiKeys: () => request<MyApiKey[]>("/me/keys"),
  createApiKey: (name: string, expires_days: number | null) =>
    request<MyApiKeyCreated>("/me/keys", {
      method: "POST",
      body: JSON.stringify({ name, expires_days }),
    }),
  revokeApiKey: (id: number) =>
    request<void>(`/me/keys/${id}`, { method: "DELETE" }),
  mySupport: () => request<MySupport[]>("/me/support"),
  submitSupport: (body: string) =>
    request<MySupport>("/me/support", {
      method: "POST",
      body: JSON.stringify({ body }),
    }),

  // Admin
  adminGetSettings: () => request<RuntimeSettings>("/admin/settings"),
  adminUpdateSettings: (patch: Partial<RuntimeSettings>) =>
    request<RuntimeSettings>("/admin/settings", {
      method: "PUT",
      body: JSON.stringify(patch),
    }),
  adminListUsers: () => request<AdminUser[]>("/admin/users"),
  adminCreateUser: (
    email: string,
    password: string,
    is_admin = false,
    display_name: string | null = null,
    can_use_mcp = false
  ) =>
    request<AdminUser>("/admin/users", {
      method: "POST",
      body: JSON.stringify({
        email,
        password,
        is_admin,
        display_name,
        can_use_mcp,
      }),
    }),
  adminPatchUser: (
    id: number,
    patch: {
      is_admin?: boolean;
      is_active?: boolean;
      password?: string;
      display_name?: string | null;
      can_use_mcp?: boolean;
    }
  ) =>
    request<AdminUser>(`/admin/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  adminListSupport: (status?: "open" | "resolved") => {
    const qs = status ? `?status=${status}` : "";
    return request<AdminSupport[]>(`/admin/support${qs}`);
  },
  adminPatchSupport: (id: number, status: "open" | "resolved") =>
    request<AdminSupport>(`/admin/support/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),
  adminDeleteUser: (id: number) =>
    request<void>(`/admin/users/${id}`, { method: "DELETE" }),
  adminStats: () => request<AdminStats>("/admin/stats"),
  adminBalance: () => request<OpenRouterBalance>("/admin/balance"),
  adminListConversations: () =>
    request<AdminConv[]>("/admin/conversations"),
  adminConvDetail: (id: number) =>
    request<AdminConvDetail>(`/admin/conversations/${id}`),
  adminListDocuments: () => request<AdminDoc[]>("/admin/documents"),
  adminReindex: () =>
    request<{ queued: boolean; started_at: string }>("/admin/reindex", {
      method: "POST",
    }),
  adminReindexStatus: () => request<ReindexStatus>("/admin/reindex/status"),

  adminReindexHtml: () =>
    request<{ queued: boolean; started_at: string }>("/admin/reindex/html", {
      method: "POST",
    }),
  adminReindexHtmlStatus: () =>
    request<ReindexHtmlStatus>("/admin/reindex/html/status"),

  adminNegativeFeedback: (limit = 100) =>
    request<NegativeFeedbackItem[]>(`/admin/feedback/negative?limit=${limit}`),

  async adminUploadDocument(
    file: File
  ): Promise<{ filename: string; bytes: number; chunks: number }> {
    const token = useAuthStore.getState().token;
    const form = new FormData();
    form.append("file", file);
    const resp = await fetch("/admin/documents/upload", {
      method: "POST",
      headers: {
        Accept: "application/json",
        // NOTE: do NOT set Content-Type; the browser will set multipart
        // boundary automatically.
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: form,
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`${resp.status}: ${text}`);
    }
    return (await resp.json()) as {
      filename: string;
      bytes: number;
      chunks: number;
    };
  },

  adminDeleteDocument: (id: number) =>
    request<void>(`/admin/documents/${id}`, { method: "DELETE" }),

  adminGitStatus: () =>
    request<{
      configured: boolean;
      url: string;
      branch: string;
      key_available: boolean;
      is_git_repo: boolean;
      head: string | null;
      last_commit_at: string | null;
      last_commit_msg: string | null;
    }>("/admin/docs/git-status"),

  adminGitPull: () =>
    request<{
      action: "clone" | "pull";
      before: string | null;
      after: string;
      changed_files: string[];
    }>("/admin/docs/git-pull", { method: "POST" }),

  adminSearchMessages: (q: string, limit = 50) => {
    const qs = new URLSearchParams({ q, limit: String(limit) });
    return request<SearchHit[]>(`/admin/messages/search?${qs}`);
  },

  adminExportUrl: (fmt: ExportFmt, conv_id?: number) =>
    conv_id
      ? `/admin/conversations/${conv_id}/export?fmt=${fmt}`
      : `/admin/export?fmt=${fmt}`,

  adminLogs: (params: { limit?: number; level?: string; q?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.level) qs.set("level", params.level);
    if (params.q) qs.set("q", params.q);
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<{
      entries: Array<Record<string, unknown>>;
      returned: number;
      file: string;
      file_size_bytes: number;
    }>(`/admin/logs${suffix}`);
  },
};
